import { useEffect, useRef, useState } from "react";

import {
  simulateMarginPlan,
  type MarginPlanInputs,
  type SimulationResult,
} from "./marginProjection";
import type {
  MarginSimulationRequest,
  MarginSimulationResponse,
} from "../workers/marginSimulation.worker";

interface CompletedSimulation {
  inputs: MarginPlanInputs;
  result: SimulationResult;
}

export interface MarginSimulationState {
  simulation: SimulationResult | null;
  simulatedInputs: MarginPlanInputs | null;
  isPending: boolean;
}

export function useMarginSimulation(
  inputs: MarginPlanInputs,
  debounceMs = 200,
): MarginSimulationState {
  const workerRef = useRef<Worker | null>(null);
  const latestRequestId = useRef(0);
  const [completed, setCompleted] = useState<CompletedSimulation | null>(null);
  const [isPending, setIsPending] = useState(true);

  useEffect(() => {
    if (typeof Worker === "undefined") return undefined;

    const worker = new Worker(
      new URL("../workers/marginSimulation.worker.ts", import.meta.url),
      { type: "module" },
    );
    worker.onmessage = (event: MessageEvent<MarginSimulationResponse>) => {
      if (event.data.requestId !== latestRequestId.current) return;
      setCompleted({ inputs: event.data.inputs, result: event.data.result });
      setIsPending(false);
    };
    workerRef.current = worker;
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;
    setIsPending(true);

    const timer = window.setTimeout(() => {
      const worker = workerRef.current;
      if (worker) {
        const request: MarginSimulationRequest = { requestId, inputs };
        worker.postMessage(request);
        return;
      }

      // Workers are unavailable in SSR and some test/embedded environments. Preserve
      // correctness there, while still debouncing repeated input events.
      const result = simulateMarginPlan(inputs);
      if (requestId !== latestRequestId.current) return;
      setCompleted({ inputs, result });
      setIsPending(false);
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [debounceMs, inputs]);

  return {
    simulation: completed?.result ?? null,
    simulatedInputs: completed?.inputs ?? null,
    isPending,
  };
}
